<template>
    <div class="album-structure-node" :class="{ 'is-root': isRoot }">
        <!-- Paper Node -->
        <div v-if="node.type === 'paper'" class="resource-row paper-row">
            <div class="col-name" :style="{ paddingLeft: depth * 24 + 'px' }">
                <el-checkbox v-model="node.checked" @change="handleCheck"></el-checkbox>
                <div class="icon-box paper-icon">
                    <el-icon><Document /></el-icon>
                </div>
                <span class="paper-name clickable" @click="emitView(node.paperId)">{{ node.label }}</span>
                <el-tag size="small" type="danger" effect="plain" v-if="node.paperMeta" style="margin-left: 8px">{{ node.paperMeta }}</el-tag>
            </div>
            <div class="col-meta">
                <span class="date">{{ formatDate(node.createdAt || node.CreatedAt, true) }}</span>
                <span class="price">3 储值</span>
                <span class="downloads">{{ node.downloads || 0 }}下载</span>
                <div class="actions">
                    <el-button link type="primary" @click="emitView(node.paperId)"><el-icon>
                            <View />
                        </el-icon></el-button>
                    <el-button link type="primary"><el-icon>
                            <ShoppingCart />
                        </el-icon></el-button>
                </div>
            </div>
        </div>

        <!-- Folder Node -->
        <div v-else-if="node.type === 'folder'" class="resource-row folder-row">
            <div class="col-name folder-col" :style="{ paddingLeft: depth * 24 + 'px' }">
                <el-checkbox v-model="node.checked" @change="handleFolderCheck"></el-checkbox>
                <div class="icon-box folder-icon">
                    <el-icon><Folder /></el-icon>
                </div>
                <span class="folder-name">{{ node.label }}</span>
                <span class="folder-count" v-if="node.children && node.children.length">({{ node.children.length }})</span>
            </div>
            
            <div class="col-meta">
                <!-- Empty placeholders for alignment parity with paper meta -->
                <span class="date">-</span>
                <span class="price">-</span>
                <span class="downloads">-</span>
                <div class="actions"></div>
            </div>
        </div>
        
        <!-- Children recursively rendered -->
        <div v-if="node.type === 'folder' && node.children && node.children.length > 0" class="children-wrapper">
            <!-- Connecting vertical line -->
            <div class="tree-line" :style="{ left: (depth * 24 + 11) + 'px' }"></div>
            
            <AlbumStructureNode v-for="child in node.children" :key="child.id" :node="child" :depth="depth + 1"
                @node-change="emitChange" @view-paper="emitView" :is-root="false" />
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue'
import { Document, View, ShoppingCart, Folder } from '@element-plus/icons-vue'

const props = defineProps({
    node: {
        type: Object,
        required: true
    },
    depth: {
        type: Number,
        default: 0
    },
    isRoot: {
        type: Boolean,
        default: true
    }
})

const emits = defineEmits(['node-change', 'view-paper'])

const formatDate = (dateStr, showDateOnly = false) => {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    const formatted = showDateOnly ? d.toLocaleDateString() : d.toLocaleString()
    return formatted === 'Invalid Date' ? '-' : formatted
}

const handleCheck = () => {
    emits('node-change')
}

const handleFolderCheck = (val) => {
    // Check/Uncheck all children recursively
    toggleChildren(props.node, val)
    emits('node-change')
}

const toggleChildren = (node, checked) => {
    if (node.children) {
        node.children.forEach(child => {
            child.checked = checked
            toggleChildren(child, checked)
        })
    }
}

const emitChange = () => {
    emits('node-change')
}

const emitView = (id) => {
    emits('view-paper', id)
}
</script>

<style scoped>
.album-structure-node {
    position: relative;
}

.resource-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 20px;
    border-bottom: 1px solid #f0f2f5;
    transition: all 0.25s ease;
    background-color: #ffffff;
    position: relative;
    z-index: 2;
}

.resource-row:hover {
    background-color: #f5f7fa;
}

.folder-row {
    background-color: #fafbfc;
}

.col-name {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    position: relative;
}

.icon-box {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.paper-icon {
    background-color: #fdf5e6;
    color: #e6a23c;
}

.folder-icon {
    background-color: #f4f4f5;
    color: #909399;
}

.paper-name {
    color: #303133;
    font-weight: 500;
    line-height: 1.4;
}

.folder-name {
    color: #303133;
    font-weight: 600;
}

.folder-count {
    color: #909399;
    font-size: 12px;
    margin-left: -4px;
}

.paper-name.clickable:hover {
    color: #409eff;
    cursor: pointer;
}

.col-meta {
    display: flex;
    gap: 20px;
    align-items: center;
    font-size: 13px;
    color: #909399;
    flex-shrink: 0;
}

.col-meta span {
    width: 100px;
    text-align: center;
}

.col-meta .actions {
    width: 80px;
    display: flex;
    justify-content: center;
    gap: 12px;
    opacity: 0;
    transition: opacity 0.25s;
}

.resource-row:hover .actions {
    opacity: 1;
}

.children-wrapper {
    position: relative;
}

.tree-line {
    position: absolute;
    top: 0;
    bottom: 22px; /* Stop before the last item ends completely */
    width: 1px;
    border-left: 1px dashed #dcdfe6;
    z-index: 1;
}

/* Specific styling for the list to ensure crisp borders */
.is-root > .resource-row:last-child {
    border-bottom: none;
}
</style>
